// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

// Service
import { WebService } from '../web/web.service';
import { UtilsService } from '../utils/utils.service';

// Classes/Data/Interface/Others
import { environment } from '../../../environments/environment';
import { UserCls,ManagerCls, WHAT, TransactionCls, VoucherCls, Redeem } from 'src/app/shared/classes';
import { allCountries } from '../../shared/countries';
import { accountType, planType } from 'src/app/shared/data-tables';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class AdminService {
  private adminUrl = environment.apiUrlWOVersion + 'favoriotadmin/';
  private paymentUrl = environment.favoriotPaymentUrl;
  private paymentAltUrl = environment.favoriotPaymentUrl2;
  private summaryDataUrl = environment.summaryDataUrl;
  private summaryUrl = environment.apiUrlWOVersion+'v2/summary';
  private licenseUrl = environment.licenseUrl;
  private analyticUrl = environment.analyticUrl;

  private allSubscriptionYears = [];

  constructor(private http: HttpClient, private web: WebService, private utils: UtilsService) { }

  // GET REQUEST ONLY
  public GET(getWhat: number, year?, type?,userid?,apikey?,query?): Promise<any> {
    switch (getWhat) {
      case WHAT.Summary:
        return this.getSummary();

      case WHAT.Users:
        return this.http.get(this.adminUrl + 'listuser?pageSize=10000')
          .toPromise()
          .then(extractData)
          .then(res => this.formatData(WHAT.Users, res))
          .catch(handleError);

      case WHAT.Admin:
        return this.http
          .get(this.adminUrl + 'managers')
          .toPromise()
          .then(extractData)
          .then(res => this.formatData(WHAT.Managers, res))
          .catch(handleError);
      
      case WHAT.userDetails:
        return this.http
          .get(this.summaryUrl,{ headers: {"Content-Type":"application/json","apikey":apikey},params:{user_id:userid} })
          .toPromise()
          .then(extractData)
          .then(res => this.formatData(WHAT.userDetails, res))
          .catch(handleError);

      case WHAT.Transactions:
        return this.http.get(this.paymentUrl + 'list_transaction', { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatData(WHAT.Transactions, res))
          .catch(handleError);

      case WHAT.Vouchers:
        return this.http.get(this.paymentAltUrl + 'voucher/list', { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatData(WHAT.Vouchers, res))
          .catch(handleError);

      case WHAT.SummaryByYear:
        return this.http.get(this.summaryDataUrl + 'summaryByYear', { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatChartData(WHAT.SummaryByYear, res))
          .catch(handleError);

      case WHAT.SummaryByMonth:
        return this.http.get(this.summaryDataUrl + 'summaryByMonth?year=' + year, { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatChartData(WHAT.SummaryByMonth, res))
          .catch(handleError);

      case WHAT.SummaryByMonthYear:
        return this.http.get(this.summaryDataUrl + 'summaryMonthYear?table=' + type, { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatChartData(WHAT.SummaryByMonthYear, res, type))
          .catch(handleError);

      case WHAT.Country:
        return this.http.get(this.summaryDataUrl + 'summary_by_country?table=users', { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatChartData(WHAT.Country, res))
          .catch(handleError);

      case WHAT.CountryAlt:
        return this.http.get(this.summaryDataUrl + 'summary_by_country?table=users', { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatData(WHAT.CountryAlt, res))
          .catch(handleError);

      case WHAT.RedeemList:
        return this.http.get(this.paymentAltUrl + 'referral/redeemlist', { headers: this.web.generateHeaders() })
          .toPromise()
          .then(extractPaymentData)
          .then(res => this.formatData(WHAT.RedeemList, res))
          .catch(handleError);
          
      case WHAT.Licenses:
        return this.http.get(this.licenseUrl+'/license',{headers: this.web.generateHeaders()})
        .toPromise()
        .then(extractData)
        .then(res => this.formatData(WHAT.Licenses,res))  
        

      case WHAT.UsersAnalysis:
        return this.http.get(this.analyticUrl+'/favoriotadmin/users?'+query,{headers: this.web.generateHeaders()})
        .toPromise()
        .then(extractData)
        default:
        break;
    }
  }

  // gets a summary of items
  public async getSummary(): Promise<any> {
    let summaryRes;
    let paymentRes;
    let voucherRes;
    let countryRes;

    try {
      summaryRes = await this.http.get(this.adminUrl + 'summary', { headers: this.web.generateHeaders() })
        .toPromise()
        .then(extractData)
        .catch(handleError);

      paymentRes = await this.http.get(this.paymentUrl + 'list_transaction', { headers: this.web.generateHeaders() })
        .toPromise()
        .then(extractPaymentData)
        .catch(handleError);

      voucherRes = await this.http.get(this.paymentAltUrl + 'voucher/list', { headers: this.web.generateHeaders() })
        .toPromise()
        .then(extractPaymentData)
        .catch(handleError);

      countryRes = await this.http.get(this.summaryDataUrl + 'summary_by_country?table=users', { headers: this.web.generateHeaders() })
        .toPromise()
        .then(extractPaymentData)
        .catch(handleError);

    } catch (e) {
      throw e;
    }

    // check if it contains any data
    // on both variables
    if (!((summaryRes instanceof Error) && (paymentRes instanceof Error))) {
      // bundle a new object that combines both of them
      let summaryData = {};
      summaryData = summaryRes;
      summaryData['transactions'] = String(paymentRes.length);
      summaryData['vouchers'] = String(voucherRes.length);
      summaryData['countrys'] = String(countryRes.total_country);
      // and return it as a promise (resolve)

      return Promise.resolve(summaryData);
    } else {
      return Promise.reject('Failed to get summary data');
    }
  }

  // create user by POST to server
  public createTheUser(user): Promise<any> {
    return this.http.post(this.adminUrl + 'createuser', JSON.stringify(user), { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(error => spitError(error));
  }

  public createTheManager(manager): Promise<any> {
    return this.http.post(this.adminUrl + '/managers', JSON.stringify(manager), { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(error => spitError(error));
  }

  // format data
  private formatData(what, res) {
    const dataArray = res;
    let formattedArray: Array<any> = [];

    switch (what) {
      case WHAT.Users:
        for (let da = 0; da < res.length; da++) {
         const user = new UserCls(
          dataArray[da].full_name,
          dataArray[da].first_name,
          dataArray[da].last_name,
          dataArray[da].user_id,
          dataArray[da].admin_id,
          dataArray[da].company,
          dataArray[da].country,
          dataArray[da].account_type,
          dataArray[da].email,
          dataArray[da].register_at,
          dataArray[da].rw_api,
          dataArray[da].read_api,
          dataArray[da].subscription,
          dataArray[da].expired_date,
          dataArray[da].trial
          );
          formattedArray.push(user);
        }
        formattedArray = this.utils.sortArrayByDate(formattedArray);
        return formattedArray;

        case WHAT.Managers:
        
         for (let da = 0; da < dataArray.length; da++) {
           
           const manager = new ManagerCls(dataArray[da]);
           if(manager['start_date'] != "unavailable" || manager['expire_dare'] == "unavailable"){
            manager['start_date'] = moment(manager['start_date']).format('MMMM D YYYY h:mm:ss A');
            manager['expire_date'] = moment(manager['expire_date']).format('MMMM D YYYY h:mm:ss A');
           }
           formattedArray.push(manager);
          }

          
          return formattedArray;

        case WHAT.userDetails:
         let summaryData = {
            projects: res.projects,
            applications:res.applications,
            groups:res.groups,
            devices:res.devices,
            device_online : 0
         }
         let countOnlineDevice = 0;
         let todayDate =  new Date().toISOString().slice(0, 10);
         let deviceDateAccess = "";
         for (let i = 0; i<res.deviceList.length;i++){
              if(res.deviceList[i].current_access != 'no record'){
               deviceDateAccess =  new Date(res.deviceList[i].current_access).toISOString().slice(0,10);
              }
             if(res.deviceList[i].connectivity == true && deviceDateAccess == todayDate){
                countOnlineDevice++;
             }
         }

         summaryData.device_online = countOnlineDevice;
         return summaryData;

      case WHAT.Transactions:
        for (let da = 0; da < dataArray.length; da++) {
          const transactionObj = JSON.parse(dataArray[da].transaction);
          const statusObj = JSON.parse(dataArray[da].status);

          const user_id = dataArray[da].user_id
          const name = transactionObj.customerName || transactionObj.name;
          const email = transactionObj.customerEmail || transactionObj.email;
          const date = this.utils.returnDate(transactionObj.transaction_created_at, '') || '';
          const orderId = dataArray[da].order_id || '';
          const item = this.utils.returnItem(transactionObj.subscriptionType, 'all');
          const price = transactionObj.price || '';
          const payType = statusObj.PAY_TYPE || statusObj.pay_type || '';
          const address = transactionObj.address || '';
          const phone = transactionObj != undefined ? transactionObj.phone : transactionObj.mobile;
          let plan_type = "No Plan";
          if(transactionObj.subscriptionType.account != undefined){
            if(transactionObj.subscriptionType.account.hasOwnProperty('plan_type')){
              plan_type = transactionObj.subscriptionType.account.plan_type != undefined ? transactionObj.subscriptionType.account.plan_type : "No Plan";
           
            }
          }
         
          const transaction = new TransactionCls(user_id,name, email, date, orderId, item, price, payType,address,phone,plan_type);

          formattedArray.push(transaction);
        }

        // sort by date
        formattedArray = this.utils.sortArrayByDate(formattedArray);
        return formattedArray;

      case WHAT.Vouchers:
        for (let da = 0; da < dataArray.length; da++) {
          if (dataArray[da].voucher_type === 'iot' || dataArray[da].voucher_type === 'iot-platform') {
            const id = dataArray[da].voucherid;
            const type = this.utils.returnVoucherUseFor(dataArray[da].voucher_type);
            const code = dataArray[da].voucher_code;
            const discount = dataArray[da].discount + '%';
            let count = 0;
            const category = dataArray[da].category;
            let status = '';
            let date = '';
            let expired = '';
            let totalCount = 0;

            if (dataArray[da].date_created || dataArray[da].date_expired) {
              date = this.utils.returnDate(dataArray[da].date_created, '');
              expired = this.utils.returnDate(dataArray[da].date_expired, '');
              status = new Date(expired).getTime() < new Date().getTime() ? 'ended' : 'live';

            } else if (typeof dataArray[da].date_created === null) {
              date = null;
              status = dataArray[da].status;
            } else if (typeof dataArray[da].date_expired === null) {
              expired = null;
              status = dataArray[da].status;
            }

            let descriptionObj = null;
            let extendYear = '';
            let isTraining = false;
            let duration = '';
            let subscription_type = '';
            let account_type ='';
            let plan_type = '';

            if (dataArray[da].hasOwnProperty('description') && typeof dataArray[da].description === 'string') {
              try {
                descriptionObj = JSON.parse(dataArray[da].description);
              } catch (e) {
                descriptionObj = { extendYear: 0, isTraining: false };
              }
            } else if (dataArray[da].hasOwnProperty('description') && typeof dataArray[da].description === 'object') {
              descriptionObj = dataArray[da].description;
            }

            if (descriptionObj !== null && descriptionObj.hasOwnProperty('extendYear')) {
               extendYear = descriptionObj.extendYear;
               subscription_type = 'yearly';
               duration = extendYear;
  
              if (this.allSubscriptionYears.indexOf(Number(extendYear)) === -1) {
                  this.allSubscriptionYears.push(Number(extendYear));
              }

            }
            if(descriptionObj !== null && descriptionObj.hasOwnProperty('duration')){
               subscription_type = descriptionObj.subscription_type;
               duration = descriptionObj.duration;
               account_type = descriptionObj.account_type;
               plan_type = account_type+"_"+subscription_type+"_"+duration ; 

              if (this.allSubscriptionYears.indexOf(Number(duration)) === -1 && subscription_type == 'yearly') {
                  this.allSubscriptionYears.push(Number(duration));
              }
               
            }

            else if (descriptionObj === null) {
              extendYear = null;
            }

            if (descriptionObj !== null && descriptionObj.hasOwnProperty('isTraining')) {
              isTraining = descriptionObj.isTraining === 'false' ? false : true;
            } else if (descriptionObj === null) {
              isTraining = false;
            }

            if (descriptionObj !== null && descriptionObj.hasOwnProperty('totalCount')) {
              totalCount = Number(descriptionObj.totalCount);
              count = totalCount - this.utils.voucherIsClaimed(dataArray[da].count)
            }

            if (descriptionObj !== null && descriptionObj.hasOwnProperty('plan_type')) {
               plan_type = descriptionObj.plan_type;
               account_type = descriptionObj.plan_type.split("_")[0];
              }

            const voucher = new VoucherCls(id, type, code, discount, extendYear, date, expired, count, isTraining, status, category, totalCount,duration,subscription_type,account_type,plan_type);
            formattedArray.push(voucher);
          }
        }
        // sort
        formattedArray = this.utils.sortArrayByDate(formattedArray);
        return formattedArray;

      case WHAT.RedeemList:
        for (let da = 0; da < dataArray.length; da++) {
          const id = dataArray[da].redeem_id;
          const fullName = dataArray[da].full_name;
          const accountNo = dataArray[da].acc_no;
          const amount = dataArray[da].amount;
          const bank_name = dataArray[da].bank_name;
          const date = this.utils.returnDate(dataArray[da].created_at, '');
          const email = dataArray[da].email;
          const userid = dataArray[da].user_id;
          const phone = dataArray[da].phone;
          const refCode = dataArray[da].referral_code;
          const status = dataArray[da].status;
          const refsIds = dataArray[da].referral_ids;

          const redeem = new Redeem(id, accountNo, amount, bank_name, date, fullName, userid, email, phone, refCode, status, refsIds);

          formattedArray.push(redeem);
        }

        // sort it
        formattedArray = this.utils.sortArrayByDate(formattedArray);

        return formattedArray;

      case WHAT.CountryAlt:
        allCountries.forEach(country => {
          if (dataArray.list_country.hasOwnProperty(country.type)) {
            formattedArray.push({ name: country.name, value: dataArray.list_country[country.type] });
          }
        });
     
        return formattedArray;

      case WHAT.Licenses:
        for (let i = 0; i < dataArray.length; i++) {
             let temp = this.utils.returnDate(dataArray[i].created_at, '');
             let temp1 = this.utils.returnDate(dataArray[i].expired_date,'');
             dataArray[i].created_at = temp;
             dataArray[i].expired_date = temp1;
        }
        formattedArray = dataArray;
        return formattedArray;
    }
  }

  // format chart data
  private formatChartData(which, res, type?) {
    const tempChartData = [];
    const chartData = res;
    let dataTemplate;
    switch (which) {
      case WHAT.SummaryByYear:
      case WHAT.SummaryByMonth:
        const keys = Object.keys(chartData);

        keys.forEach(key => {
          dataTemplate = { name: '', data: [] };

          const dataKeys = Object.keys(chartData[key]);
          dataTemplate.name = key;

          for (let dk = 0; dk < dataKeys.length; dk++) {
            const dataTemplateInner = { x: '', y: 0 };
            dataTemplateInner.x = dataKeys[dk];
            dataTemplateInner.y = chartData[key][dataKeys[dk]];
            dataTemplate.data.push(dataTemplateInner);
          }

          tempChartData.push(dataTemplate);
        });

        if (tempChartData.length >= 1) {
          return Promise.resolve(tempChartData);
        } else {
          return Promise.reject('No data received');
        }

      case WHAT.SummaryByMonthYear:

        const objectKeys = Object.keys(chartData);

        dataTemplate = { name: type, data: [] };

        objectKeys.forEach(el => {
          const dataTemplateInner = { x: '', y: 0 };
          dataTemplateInner.x = el.replace('_', ' ');
          dataTemplateInner.y = chartData[el];
          dataTemplate.data.push(dataTemplateInner);
        });

        tempChartData.push(dataTemplate);

        if (tempChartData.length >= 1) {
          return Promise.resolve(tempChartData);
        } else {
          return Promise.reject('No data received');
        }

      case WHAT.Country:
        const countryData = chartData.list_country;

        const objectCountryKeys = Object.keys(countryData);
        const countryValues = [];
        const countryName = [];

        objectCountryKeys.forEach(el => {
          allCountries.forEach(el2 => {

            if (el === el2.type) {
              countryValues.push(countryData[el]);
              countryName.push(el2.name);
            }
          });
        });


        if (objectCountryKeys.length >= 1 && countryValues.length >= 1) {
          const countryChart = [{ countries: countryName, values: countryValues }];
          return Promise.resolve(countryChart);
        } else {
          return Promise.reject('No data received');
        }

      default:
        break;
    }
  }

  // update the user
  public updateTheUser(user, user_id) {
    return this.http.post(this.adminUrl + 'edituser?user_id=' + user_id, JSON.stringify(user), { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(err => spitError(err));
  }

  // update the admin
  public updateTheAdmin(user, user_id) {
    return this.http.post(this.adminUrl + 'editadminuser?user_id=' + user_id, JSON.stringify(user), { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(err => spitError(err));
  }

    // update the admin
    public updateTheManager(manager) {
      return this.http.put(this.adminUrl + 'managers', JSON.stringify(manager), { headers: this.web.generateHeaders() })
        .toPromise()
        .then(res => spitResponse(res))
        .catch(err => spitError(err));
    }

  // delete the user
  public deleteTheUser(user_id) {
    return this.http.post(this.adminUrl + 'deleteuser?user_id=' + user_id, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(err => spitError(err));
  }

  // delete the admin
  public deleteTheAdmin(user_id) {
    return this.http.post(this.adminUrl + 'deleteadminuser?user_id=' + user_id, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(err => spitError(err));
  }

  public deleteTheManager(manager) {
    return this.http.delete(this.adminUrl + 'managers?admin_id='+manager.admin_id, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(err => spitError(err));
  }

  // confirms redeem transaction transfer has been made
  public setRedeemStatus(confirmRedeem) {
    return this.http.post(this.paymentAltUrl + 'referral/admin', confirmRedeem, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(spitResponse)
      .catch(handleError);
  }

  // get voucher
  public submitNewVoucher(voucherObj) {
    const header = this.web.generateHeaders();

    return this.http.post(this.paymentAltUrl + 'voucher/create', voucherObj, { headers: header })
      .toPromise()
      .then(spitResponse)
      .catch(handleError);
  }

  // update voucher
  public updateVoucher(voucher) {
    return this.http.post(this.paymentAltUrl + 'voucher/edit', voucher, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(spitResponse)
      .catch(handleError);
  }

  // delete voucher
  public deleteVoucher(voucherId) {
    return this.http.delete(this.paymentAltUrl + 'voucher?voucherid=' + voucherId, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(spitResponse)
      .catch(handleError);
  }
  
  // create license
  public createLicense(info): Promise<any> {
    return this.http.post(this.licenseUrl + '/license/create', JSON.stringify(info), { headers: this.web.generateHeaders() })
      .toPromise()
      .then(res => spitResponse(res))
      .catch(error => spitError(error));
  }

    // update license
  public updateLicense(info) {
      return this.http.post(this.licenseUrl + '/license/update',info, { headers: this.web.generateHeaders() })
        .toPromise()
        .then(spitResponse)
        .catch(handleError);
    }
  
    // delete license
  public deleteLicense(id) {
    return this.http.delete(this.licenseUrl + '/license/delete?id=' + id, { headers: this.web.generateHeaders() })
      .toPromise()
      .then(spitResponse)
      .catch(handleError);
  }
  // get admin login
  public getLoginDeets() {
    const login = JSON.parse(localStorage.getItem('user'));

    return login;
  }

  // removes users data
  public loggingOut() {
    // remove localstorage data
    localStorage.removeItem('user');

    return Promise.resolve();
  }

  public getSubscriptionYears() {
    return this.allSubscriptionYears.sort((a, b) => {
      return a - b;
    });
  }
}

// extract the response data of summary
function extractData(res) {
  if (res.code === 200) {
    return res.result;
  } else if (res.statusCode === 200) {
    return res.results;
  } else {
    return {};
  }
}

// spit out any message
function spitResponse(res) {
  if (res.statusCode === 201) {
    return Promise.resolve(res.message);
  }

  if (res.statusCode === 200) {
    return Promise.resolve(res.message);
  }

  if (res.code === 200 && !(res.hasOwnProperty('results')) && (res.hasOwnProperty('message'))) {
    return Promise.resolve(res.message);
  }

  if (res.code === 200 && (res.hasOwnProperty('results')) && (res.hasOwnProperty('message'))) {
    return Promise.resolve({ message: res.message, results: res.results });
  }
}


// spit out errors
function spitError(err) {
  console.log(err);
  return Promise.reject(err.error.message);
}

// extract payment response data
function extractPaymentData(res) {
  return res.results;
}

// handle any errors
function handleError(error) {
  console.log(error);
  return error;
}
